// src/pages/User/Profile.jsx
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../../components/Sidebar";
import Header from "../../components/Header";
import Swal from "sweetalert2";
import { useAuth } from "../../contexts/AuthContext";
import { updateMyAvatar, updateMyPassword, updateMyName, updateMyAcademicProfile, apiClient } from "../../services/api";

// --- AVATAR CONFIGURATION DATA ---
const BG_COLORS = [
  { id: "b6e3f4", name: "Sky Blue", hex: "#b6e3f4" },
  { id: "c0aede", name: "Purple", hex: "#c0aede" },
  { id: "ffdfbf", name: "Peach", hex: "#ffdfbf" },
  { id: "d1d4f9", name: "Lavender", hex: "#d1d4f9" },
  { id: "ffd5dc", name: "Pink", hex: "#ffd5dc" },
  { id: "transparent", name: "Transparent", hex: "transparent" },
];

const Profile = () => {
  const navigate = useNavigate();
  const { user, loading, logout, setAuthUser } = useAuth();

  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [avatarUrl, setAvatarUrl] = useState("");
  const [isAvatarModalOpen, setIsAvatarModalOpen] = useState(false);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [isEditNameOpen, setIsEditNameOpen] = useState(false);
  const [isAcademicModalOpen, setIsAcademicModalOpen] = useState(false);
  const [isBugReportModalOpen, setIsBugReportModalOpen] = useState(false);

  const toggleSidebar = () => setIsSidebarOpen((s) => !s);

  useEffect(() => {
    if (!loading && !user) navigate("/login", { replace: true });
  }, [loading, user, navigate]);

  useEffect(() => {
    if (!user) return;
    const initialSeed = user.firstName || "User";
    const defaultAvatar = `https://api.dicebear.com/9.x/adventurer/svg?seed=${encodeURIComponent(
      initialSeed
    )}&backgroundColor=b6e3f4`;
    setAvatarUrl(user.avatar || defaultAvatar);
  }, [user]);

  const handleLogout = () => {
    Swal.fire({
      title: "Sign out?",
      text: "You will need to login again to access your account.",
      icon: "question",
      showCancelButton: true,
      confirmButtonColor: "#2563eb",
      cancelButtonColor: "#94a3b8",
      confirmButtonText: "Yes, Sign Out",
    }).then(async (result) => {
      if (!result.isConfirmed) return;
      await logout();
      navigate("/login", { replace: true });
    });
  };

  const handleSaveAvatar = async (newUrl) => {
    try {
      setAvatarUrl(newUrl);

      // ✅ new API: PUT /api/users/me/avatar { avatarUrl }
      const res = await updateMyAvatar(newUrl);

      // backend returns: { success:true, user: {...} } OR could return user directly
      const updatedUser = res?.data ?? res?.user ?? res ?? null;

      if (updatedUser) {
        setAuthUser(updatedUser);
      } else {
        setAuthUser((prev) => (prev ? { ...prev, avatar: newUrl } : prev));
      }

      Swal.fire({
        icon: "success",
        title: "Saved!",
        text: "Your avatar has been updated.",
        timer: 1200,
        showConfirmButton: false,
      });

      setIsAvatarModalOpen(false);
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Avatar update failed",
        text: err?.message || "Please try again.",
      });
    }
  };

  const handleSaveName = async ({ firstName, lastName }) => {
    try {
      const res = await updateMyName({ firstName, lastName });
      const updatedUser = res?.data ?? res?.user ?? res ?? null;

      if (updatedUser) {
        setAuthUser(updatedUser);
      } else {
        setAuthUser((prev) => (prev ? { ...prev, firstName, lastName } : prev));
      }

      Swal.fire({
        icon: "success",
        title: "Updated!",
        text: "Your name has been updated.",
        timer: 1200,
        showConfirmButton: false,
      });

      setIsEditNameOpen(false);
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Update failed",
        text: err?.message || "Please try again.",
      });
    }
  };

  const handleSaveAcademic = async ({ course, yearLevel, interests }) => {
    try {
      const res = await updateMyAcademicProfile({ course, yearLevel, interests });
      const updatedUser = res?.user ?? res?.data ?? null;
      if (updatedUser) {
        setAuthUser(updatedUser);
      } else {
        setAuthUser((prev) => prev ? { ...prev, course, yearLevel, interests } : prev);
      }
      Swal.fire({ icon: "success", title: "Saved!", text: "Academic profile updated.", timer: 1200, showConfirmButton: false });
      setIsAcademicModalOpen(false);
    } catch (err) {
      Swal.fire({ icon: "error", title: "Update failed", text: err?.message || "Please try again." });
    }
  };

  const handleChangePassword = async (passwords) => {
    try {
      await updateMyPassword({
        currentPassword: passwords.currentPassword,
        newPassword: passwords.newPassword,
      });

      Swal.fire({
        icon: "success",
        title: "Password Updated",
        text: "Your security credentials have been updated successfully.",
        timer: 2000,
        showConfirmButton: false,
      });

      setIsPasswordModalOpen(false);
    } catch (err) {
      Swal.fire({
        icon: "error",
        title: "Update Failed",
        text:
          err?.response?.data?.message ||
          err?.message ||
          "Check your current password and try again.",
      });
    }
  };

  if (loading) return <div className="p-6">Loading...</div>;
  if (!user) return null;

  return (
    <div className="bg-slate-50 text-slate-800 font-sans antialiased flex h-screen overflow-hidden">
      <Sidebar isOpen={isSidebarOpen} toggleSidebar={toggleSidebar} />

      <main className="flex-1 flex flex-col h-full overflow-hidden relative">
        <Header toggleSidebar={toggleSidebar} />

        <div className="flex-1 overflow-y-auto p-4 md:p-8">
          <div className="max-w-6xl mx-auto w-full space-y-8 animate-fade-in pb-20">
            <ProfileHeader
              user={user}
              avatarUrl={avatarUrl}
              onEditAvatar={() => setIsAvatarModalOpen(true)}
            />

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              <div className="lg:col-span-2 space-y-6">
                <PersonalInfoCard user={user} onEdit={() => setIsEditNameOpen(true)} />
                <AcademicProfileCard user={user} onEdit={() => setIsAcademicModalOpen(true)} />
              </div>

              <div className="space-y-6 lg:sticky lg:top-6 self-start">
                <SecurityCard
                  onLogout={handleLogout}
                  onChangePassword={() => setIsPasswordModalOpen(true)}
                />
                <SupportCard
                  onReportBug={() => setIsBugReportModalOpen(true)}
                />
              </div>
            </div>
          </div>
        </div>
      </main>

      {isAvatarModalOpen && (
        <AvatarModal
          onClose={() => setIsAvatarModalOpen(false)}
          onSave={handleSaveAvatar}
          initialSeed={user.firstName}
        />
      )}

      {isPasswordModalOpen && (
        <ChangePasswordModal
          onClose={() => setIsPasswordModalOpen(false)}
          onSave={handleChangePassword}
        />
      )}

      {isEditNameOpen && (
        <EditNameModal
          onClose={() => setIsEditNameOpen(false)}
          onSave={handleSaveName}
          initialFirstName={user.firstName || ""}
          initialLastName={user.lastName || ""}
        />
      )}

      {isAcademicModalOpen && (
        <AcademicModal
          onClose={() => setIsAcademicModalOpen(false)}
          onSave={handleSaveAcademic}
          initialCourse={user.course || ""}
          initialYearLevel={user.yearLevel || ""}
          initialInterests={user.interests || []}
        />
      )}

      {isBugReportModalOpen && (
        <BugReportModal
          isOpen={isBugReportModalOpen}
          onClose={() => setIsBugReportModalOpen(false)}
          user={user}
        />
      )}
    </div>
  );
};

// --- SUB-COMPONENTS ---

const ProfileHeader = ({ user, avatarUrl, onEditAvatar }) => {
  return (
    <div className="bg-white rounded-3xl border border-slate-100 shadow-xl shadow-slate-200/50 overflow-hidden relative group">
      <div className="h-48 bg-gradient-to-r from-blue-700 via-blue-600 to-indigo-600 relative overflow-hidden">
        <div className="absolute inset-0 bg-[url('https://www.transparenttextures.com/patterns/cubes.png')] opacity-10"></div>
        <div className="absolute -bottom-10 -right-10 w-64 h-64 bg-white opacity-10 rounded-full blur-3xl"></div>
      </div>

      <div className="relative px-8 pb-8 flex flex-col items-center -mt-16 z-10">
        <div className="w-32 h-32 rounded-full p-1 shadow-xl relative bg-white mb-4">
          <img
            src={avatarUrl}
            alt="Avatar"
            className="w-full h-full rounded-full object-cover border-4 border-white bg-blue-50"
          />
          <button
            onClick={onEditAvatar}
            className="absolute bottom-1 right-1 bg-slate-900 text-white w-8 h-8 flex items-center justify-center rounded-full text-xs shadow-md border-2 border-white hover:scale-110 transition cursor-pointer hover:bg-blue-600"
          >
            <i className="fa-solid fa-pen"></i>
          </button>
        </div>

        <div className="text-center mb-2">
          <h1 className="text-3xl font-bold text-slate-800 flex items-center justify-center gap-2 mb-1">
            {user.firstName || "Student"} {user.lastName || ""}
            {user.isVerified && (
              <i
                className="fa-solid fa-circle-check text-blue-500 text-xl"
                title="Verified Student"
              ></i>
            )}
          </h1>

          <p className="text-slate-400 text-sm mt-1 flex items-center justify-center gap-2">
            <i className="fa-regular fa-envelope"></i> {user.email}
          </p>
        </div>
      </div>
    </div>
  );
};

const PersonalInfoCard = ({ user, onEdit }) => (
  <div className="bg-white border border-slate-100 p-6 md:p-8 rounded-3xl shadow-sm">
    <div className="flex justify-between items-center mb-8">
      <h3 className="font-bold text-slate-800 text-lg md:text-xl flex items-center gap-3">
        <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-blue-50 flex items-center justify-center text-blue-600">
          <i className="fa-regular fa-id-card"></i>
        </div>
        Personal Info
      </h3>

      <button
        onClick={onEdit}
        className="px-3 py-1.5 md:px-4 md:py-2 bg-slate-50 text-slate-600 hover:bg-slate-100 hover:text-blue-600 rounded-lg md:rounded-xl text-xs md:text-sm font-bold transition flex items-center gap-1 md:gap-2 border border-slate-200"
      >
        <i className="fa-solid fa-pen-to-square"></i>
        Edit <span className="hidden sm:inline">Details</span>
      </button>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
      <div className="space-y-2">
        <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">
          First Name
        </label>
        <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 text-slate-700 font-semibold">
          {user.firstName || user.fullName || "Not Set"}
        </div>
      </div>

      <div className="space-y-2">
        <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">
          Last Name
        </label>
        <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 text-slate-700 font-semibold">
          {user.lastName || "-"}
        </div>
      </div>

      <div className="space-y-2 md:col-span-2">
        <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">
          Email Address
        </label>
        <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 text-slate-700 font-semibold flex items-center justify-between">
          <span className="truncate">{user.email}</span>
          <i className="fa-solid fa-lock text-slate-300 flex-shrink-0 ml-2"></i>
        </div>
      </div>
    </div>
  </div>
);

const SecurityCard = ({ onLogout, onChangePassword }) => (
  <div className="bg-white border border-slate-100 p-6 md:p-7 rounded-3xl shadow-sm hover:shadow-md transition-shadow">
    <h3 className="font-bold text-slate-800 mb-5 flex items-center gap-2.5 text-lg">
      <div className="w-8 h-8 rounded-xl bg-slate-100 flex items-center justify-center text-slate-500">
        <i className="fa-solid fa-shield-halved text-sm"></i>
      </div>
      Account Actions
    </h3>

    <div className="space-y-3">
      <button
        onClick={onChangePassword}
        className="w-full text-left p-3.5 text-sm font-bold text-slate-700 hover:bg-slate-50 border border-slate-100 hover:border-slate-200 rounded-2xl transition flex items-center justify-between group cursor-pointer"
      >
        <span className="flex items-center gap-3">
          <span className="w-8 h-8 rounded-lg bg-blue-50 text-blue-600 flex items-center justify-center group-hover:bg-blue-600 group-hover:text-white transition-colors">
            <i className="fa-solid fa-key text-xs"></i>
          </span>
          Change Password
        </span>
        <i className="fa-solid fa-chevron-right text-xs text-slate-300 group-hover:text-slate-500 group-hover:translate-x-0.5 transition-all"></i>
      </button>

      <div className="h-px bg-slate-100 my-1"></div>

      <button
        onClick={onLogout}
        className="w-full text-left p-3.5 text-sm font-bold text-red-600 bg-red-50/40 hover:bg-red-50 border border-red-100/60 hover:border-red-200 rounded-2xl transition flex items-center justify-between group cursor-pointer"
      >
        <span className="flex items-center gap-3">
          <span className="w-8 h-8 rounded-lg bg-red-100/60 text-red-600 flex items-center justify-center group-hover:bg-red-600 group-hover:text-white transition-colors">
            <i className="fa-solid fa-arrow-right-from-bracket text-xs"></i>
          </span>
          Sign Out
        </span>
        <i className="fa-solid fa-chevron-right text-xs text-red-300 group-hover:text-red-500 group-hover:translate-x-0.5 transition-all"></i>
      </button>
    </div>
  </div>
);

const SupportCard = ({ onReportBug }) => (
  <div className="bg-gradient-to-br from-white via-white to-rose-50/30 border border-slate-100 hover:border-rose-100 p-6 md:p-7 rounded-3xl shadow-sm hover:shadow-md transition-all relative overflow-hidden group">
    <div className="flex items-center justify-between mb-4">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-xl bg-rose-50 border border-rose-100 flex items-center justify-center text-rose-500">
          <i className="fa-solid fa-headset text-sm"></i>
        </div>
        <div>
          <h3 className="font-bold text-slate-800 text-base leading-tight">Help & Feedback</h3>
          <p className="text-[11px] text-slate-400">ReviewMate Support Desk</p>
        </div>
      </div>
      <span className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold bg-emerald-50 text-emerald-600 border border-emerald-100">
        <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse"></span>
        Online
      </span>
    </div>

    <p className="text-xs text-slate-500 mb-4 leading-relaxed">
      Found a bug or glitch while reviewing? Let us know so we can fix it promptly for you.
    </p>

    <button
      onClick={onReportBug}
      className="w-full p-3.5 text-sm font-bold text-slate-700 bg-white hover:bg-rose-50/70 border border-slate-200 hover:border-rose-200 rounded-2xl transition-all duration-200 flex items-center justify-between shadow-xs hover:shadow-sm cursor-pointer group/btn"
    >
      <span className="flex items-center gap-3">
        <span className="w-8 h-8 rounded-xl bg-rose-50 text-rose-500 flex items-center justify-center group-hover/btn:scale-110 group-hover/btn:bg-rose-500 group-hover/btn:text-white transition-all">
          <i className="fa-solid fa-bug text-xs"></i>
        </span>
        <span className="text-left">
          <span className="block font-bold text-slate-800 text-xs sm:text-sm">Report a Bug</span>
          <span className="block text-[11px] font-normal text-slate-400">Direct developer notification</span>
        </span>
      </span>
      <i className="fa-solid fa-arrow-right text-xs text-slate-300 group-hover/btn:text-rose-500 group-hover/btn:translate-x-1 transition-all"></i>
    </button>
  </div>
);

// ============================================================
// ✅ EDIT NAME MODAL (firstName + lastName)
// ============================================================
const EditNameModal = ({ onClose, onSave, initialFirstName, initialLastName }) => {
  const [form, setForm] = useState({ firstName: initialFirstName, lastName: initialLastName });
  const [touched, setTouched] = useState({ firstName: false, lastName: false });

  const firstName = form.firstName.trim();
  const lastName = form.lastName.trim();

  const validFirst = firstName.length > 0 && firstName.length <= 60;
  const validLast = lastName.length > 0 && lastName.length <= 60;

  const canSave = validFirst && validLast;

  const onChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
  };

  const onBlur = (e) => {
    const { name } = e.target;
    setTouched((t) => ({ ...t, [name]: true }));
  };

  const submit = (e) => {
    e.preventDefault();
    if (!canSave) {
      return Swal.fire("Error", "Please enter a valid first and last name.", "warning");
    }
    onSave({ firstName, lastName });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm transition-all animate-fade-in">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden border border-slate-100">
        <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-white">
          <h3 className="text-lg font-bold text-slate-800">Edit Details</h3>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full flex items-center justify-center text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition"
          >
            <i className="fa-solid fa-xmark text-lg"></i>
          </button>
        </div>

        <form onSubmit={submit} className="p-6 space-y-5">
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">
              First Name
            </label>
            <input
              name="firstName"
              value={form.firstName}
              onChange={onChange}
              onBlur={onBlur}
              className={`w-full px-4 py-3 rounded-xl border outline-none transition text-sm font-semibold text-slate-700 bg-slate-50 focus:bg-white ${
                touched.firstName && !validFirst
                  ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-500/10"
                  : "border-slate-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
              }`}
              placeholder="Enter first name"
            />
            {touched.firstName && !validFirst && (
              <p className="text-[11px] text-red-500 mt-1 pl-1 font-semibold">
                First name is required (max 60 chars).
              </p>
            )}
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">
              Last Name
            </label>
            <input
              name="lastName"
              value={form.lastName}
              onChange={onChange}
              onBlur={onBlur}
              className={`w-full px-4 py-3 rounded-xl border outline-none transition text-sm font-semibold text-slate-700 bg-slate-50 focus:bg-white ${
                touched.lastName && !validLast
                  ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-500/10"
                  : "border-slate-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
              }`}
              placeholder="Enter last name"
            />
            {touched.lastName && !validLast && (
              <p className="text-[11px] text-red-500 mt-1 pl-1 font-semibold">
                Last name is required (max 60 chars).
              </p>
            )}
          </div>

          <div className="pt-2 flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-3 rounded-xl font-bold text-sm text-slate-500 hover:bg-slate-50 hover:text-slate-700 transition"
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={!canSave}
              className={`flex-1 py-3 rounded-xl font-bold text-sm text-white shadow-lg transition hover:-translate-y-0.5 active:translate-y-0 ${
                !canSave
                  ? "bg-blue-400 cursor-not-allowed shadow-blue-300/20"
                  : "bg-blue-600 hover:bg-blue-700 shadow-blue-600/20"
              }`}
            >
              Save Changes
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ============================================================
// ✅ CHANGE PASSWORD MODAL (kept as-is)
// ============================================================
const ChangePasswordModal = ({ onClose, onSave }) => {
  const [passwords, setPasswords] = useState({
    currentPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const [showPass, setShowPass] = useState(false);
  const [touched, setTouched] = useState({
    currentPassword: false,
    newPassword: false,
    confirmPassword: false,
  });

  const newPass = passwords.newPassword;

  const rules = {
    length: newPass.length >= 12,
    special: /[^A-Za-z0-9\s]/.test(newPass),
    uppercase: /[A-Z]/.test(newPass),
    number: /[0-9]/.test(newPass),
  };

  const allMet = rules.length && rules.special && rules.uppercase && rules.number;
  const confirmMismatch =
    passwords.confirmPassword && passwords.newPassword !== passwords.confirmPassword;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setPasswords((p) => ({ ...p, [name]: value }));
  };

  const handleBlur = (e) => {
    const { name } = e.target;
    setTouched((t) => ({ ...t, [name]: true }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    if (!passwords.currentPassword || !passwords.newPassword || !passwords.confirmPassword) {
      return Swal.fire("Error", "All fields are required.", "warning");
    }

    if (!allMet) {
      return Swal.fire("Error", "New password does not meet all requirements.", "warning");
    }

    if (passwords.newPassword !== passwords.confirmPassword) {
      return Swal.fire("Error", "New passwords do not match.", "warning");
    }

    onSave(passwords);
  };

  const ValidationItem = ({ met, label }) => (
    <div className={`flex items-center gap-2 text-xs font-semibold ${met ? "text-emerald-600" : "text-slate-400"}`}>
      <i className={`fa-solid ${met ? "fa-circle-check" : "fa-circle"} text-[10px]`}></i>
      <span>{label}</span>
    </div>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm transition-all animate-fade-in">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md overflow-hidden border border-slate-100">
        <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center bg-white">
          <h3 className="text-lg font-bold text-slate-800">Change Password</h3>
          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full flex items-center justify-center text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition"
          >
            <i className="fa-solid fa-xmark text-lg"></i>
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">
              Current Password
            </label>
            <input
              type={showPass ? "text" : "password"}
              name="currentPassword"
              value={passwords.currentPassword}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="••••••••"
              className={`w-full px-4 py-3 rounded-xl border outline-none transition text-sm font-semibold text-slate-700 bg-slate-50 focus:bg-white ${
                touched.currentPassword && !passwords.currentPassword
                  ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-500/10"
                  : "border-slate-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
              }`}
            />
            {touched.currentPassword && !passwords.currentPassword && (
              <p className="text-[11px] text-red-500 mt-1 pl-1 font-semibold">
                Current password is required.
              </p>
            )}
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">
              New Password
            </label>
            <input
              type={showPass ? "text" : "password"}
              name="newPassword"
              value={passwords.newPassword}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="••••••••"
              className={`w-full px-4 py-3 rounded-xl border outline-none transition text-sm font-semibold text-slate-700 bg-slate-50 focus:bg-white ${
                touched.newPassword && !allMet
                  ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-500/10"
                  : "border-slate-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
              }`}
            />

            <div className="mt-3 grid grid-cols-2 gap-y-2 gap-x-4 bg-slate-50 p-3 rounded-lg border border-slate-100">
              <ValidationItem met={rules.length} label="12+ Characters (required)" />
              <ValidationItem met={rules.special} label="1 Special Character (required)" />
              <ValidationItem met={rules.uppercase} label="Uppercase Letter (required)" />
              <ValidationItem met={rules.number} label="Number (required)" />
            </div>

            {touched.newPassword && !allMet && (
              <p className="text-[11px] text-red-500 mt-2 pl-1 font-semibold">
                Please meet all password requirements.
              </p>
            )}
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">
              Confirm New Password
            </label>
            <input
              type={showPass ? "text" : "password"}
              name="confirmPassword"
              value={passwords.confirmPassword}
              onChange={handleChange}
              onBlur={handleBlur}
              placeholder="••••••••"
              className={`w-full px-4 py-3 rounded-xl border outline-none transition text-sm font-semibold text-slate-700 bg-slate-50 focus:bg-white ${
                confirmMismatch
                  ? "border-red-300 focus:border-red-500 focus:ring-4 focus:ring-red-500/10"
                  : "border-slate-200 focus:border-blue-500 focus:ring-4 focus:ring-blue-500/10"
              }`}
            />
            {confirmMismatch && (
              <p className="text-[11px] text-red-500 mt-1 pl-1 font-semibold">
                New passwords do not match.
              </p>
            )}
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="showPass"
              checked={showPass}
              onChange={() => setShowPass(!showPass)}
              className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500 cursor-pointer"
            />
            <label htmlFor="showPass" className="text-xs font-semibold text-slate-500 cursor-pointer select-none">
              Show Passwords
            </label>
          </div>

          <div className="pt-2 flex gap-3">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 py-3 rounded-xl font-bold text-sm text-slate-500 hover:bg-slate-50 hover:text-slate-700 transition"
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={
                !passwords.currentPassword ||
                !passwords.newPassword ||
                !passwords.confirmPassword ||
                !allMet ||
                confirmMismatch
              }
              className={`flex-1 py-3 rounded-xl font-bold text-sm text-white shadow-lg transition hover:-translate-y-0.5 active:translate-y-0 ${
                !passwords.currentPassword ||
                !passwords.newPassword ||
                !passwords.confirmPassword ||
                !allMet ||
                confirmMismatch
                  ? "bg-blue-400 cursor-not-allowed shadow-blue-300/20"
                  : "bg-blue-600 hover:bg-blue-700 shadow-blue-600/20"
              }`}
            >
              Update Password
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// --- AVATAR MODAL COMPONENT ---
const AvatarModal = ({ onClose, onSave, initialSeed }) => {
  const [selectedSeed, setSelectedSeed] = useState(() => initialSeed || Math.random().toString(36).substring(7));
  const [selectedBg, setSelectedBg] = useState("b6e3f4");
  const [options, setOptions] = useState([]);
  const [isRefreshing, setIsRefreshing] = useState(false);

  const generateAvatars = () => {
    setIsRefreshing(true);
    const newOptions = Array.from({ length: 12 }, () =>
      `${Math.random().toString(36).substring(2, 9)}-${Date.now().toString(36).slice(-3)}`
    );
    setOptions(newOptions);
    setSelectedSeed(newOptions[0]);
    setTimeout(() => setIsRefreshing(false), 250);
  };

  useEffect(() => {
    generateAvatars();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const buildUrl = (seed, bg) => {
    let url = `https://api.dicebear.com/9.x/adventurer/svg?seed=${encodeURIComponent(seed)}`;
    if (bg && bg !== "transparent") {
      url += `&backgroundColor=${bg}`;
    }
    return url;
  };

  const previewUrl = buildUrl(selectedSeed, selectedBg);

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm transition-all animate-fade-in">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-4xl p-6 relative flex flex-col md:flex-row gap-6 max-h-[92vh] overflow-hidden">
        {/* Mobile Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 z-10 md:hidden w-8 h-8 rounded-full bg-gray-100 flex items-center justify-center"
        >
          <i className="fa-solid fa-xmark text-lg"></i>
        </button>

        {/* Left Side: Avatar Options Grid */}
        <div className="w-full md:w-3/5 flex flex-col min-h-0">
          <div className="flex justify-between items-center mb-4 shrink-0">
            <div>
              <h3 className="text-xl font-bold text-gray-900">Choose Your Avatar</h3>
              <p className="text-xs text-gray-500 mt-0.5">Select a look that represents you best</p>
            </div>
            <button
              onClick={generateAvatars}
              disabled={isRefreshing}
              className="text-xs font-bold text-blue-600 bg-blue-50 hover:bg-blue-100 px-3 py-2 rounded-xl transition flex items-center gap-1.5 shadow-sm active:scale-95 cursor-pointer"
            >
              <i className={`fa-solid fa-arrows-rotate ${isRefreshing ? "fa-spin" : ""}`}></i>
              Refresh New
            </button>
          </div>

          <div className="flex-1 overflow-y-auto pr-1 custom-scroll grid grid-cols-3 sm:grid-cols-4 gap-3 p-1">
            {options.map((seed, index) => {
              const isSelected = selectedSeed === seed;
              const optionUrl = buildUrl(seed, selectedBg);
              return (
                <button
                  key={index}
                  type="button"
                  onClick={() => setSelectedSeed(seed)}
                  className={`group relative aspect-square rounded-2xl p-1.5 transition-all duration-200 cursor-pointer flex items-center justify-center border-2 ${
                    isSelected
                      ? "border-blue-600 bg-blue-50/60 ring-4 ring-blue-500/20 shadow-md scale-102"
                      : "border-slate-100 bg-slate-50/50 hover:border-blue-200 hover:bg-white hover:shadow-sm"
                  }`}
                >
                  <img
                    src={optionUrl}
                    alt={`Avatar option ${index + 1}`}
                    className="w-full h-full rounded-xl object-cover pointer-events-none"
                    loading="lazy"
                  />
                  {isSelected && (
                    <div className="absolute top-1.5 right-1.5 w-5 h-5 bg-blue-600 rounded-full flex items-center justify-center text-white text-[10px] shadow-sm">
                      <i className="fa-solid fa-check"></i>
                    </div>
                  )}
                </button>
              );
            })}
          </div>
        </div>

        {/* Right Side: Preview & Customization */}
        <div className="w-full md:w-2/5 flex flex-col justify-between border-t md:border-t-0 md:border-l border-gray-100 pt-4 md:pt-0 md:pl-6 shrink-0">
          <div className="hidden md:flex justify-end">
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-700 w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center transition cursor-pointer"
            >
              <i className="fa-solid fa-xmark text-lg"></i>
            </button>
          </div>

          <div className="flex flex-col items-center my-auto py-2">
            <span className="text-[11px] font-bold text-gray-400 uppercase tracking-wider mb-3">
              Selected Look Preview
            </span>
            <div className="relative group">
              <div className="w-36 h-36 rounded-full border-4 border-blue-200/60 p-1.5 bg-gradient-to-tr from-blue-100 via-indigo-50 to-blue-50 shadow-xl flex items-center justify-center">
                <img
                  src={previewUrl}
                  alt="Selected Avatar Preview"
                  className="w-full h-full rounded-full object-cover bg-white/40"
                />
              </div>
            </div>

            {/* Background Color Palette */}
            <div className="w-full mt-6">
              <label className="text-xs font-bold text-gray-700 block text-center mb-2.5">
                Background Theme
              </label>
              <div className="flex items-center justify-center gap-2.5 flex-wrap">
                {BG_COLORS.map((bg) => {
                  const isBgSelected = selectedBg === bg.id;
                  return (
                    <button
                      key={bg.id}
                      type="button"
                      onClick={() => setSelectedBg(bg.id)}
                      title={bg.name}
                      style={{
                        backgroundColor: bg.hex === "transparent" ? "#ffffff" : bg.hex,
                      }}
                      className={`w-7 h-7 rounded-full transition-all flex items-center justify-center border cursor-pointer ${
                        isBgSelected
                          ? "ring-2 ring-blue-600 ring-offset-2 scale-110 border-blue-400"
                          : "border-gray-200 hover:scale-105"
                      } ${bg.hex === "transparent" ? "relative overflow-hidden" : ""}`}
                    >
                      {bg.hex === "transparent" && (
                        <div className="absolute inset-0 flex items-center justify-center text-gray-400 text-[10px]">
                          <i className="fa-solid fa-ban"></i>
                        </div>
                      )}
                      {isBgSelected && bg.hex !== "transparent" && (
                        <i className="fa-solid fa-check text-[10px] text-gray-800"></i>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          <div className="mt-4">
            <button
              onClick={() => onSave(previewUrl)}
              className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3.5 px-4 rounded-2xl shadow-lg shadow-blue-500/25 transition-all transform active:scale-98 flex items-center justify-center gap-2 cursor-pointer"
            >
              <i className="fa-solid fa-floppy-disk"></i>
              Save Avatar
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Profile;

// ============================================================
// ✅ ACADEMIC PROFILE CARD (display)
// ============================================================
const YEAR_LEVELS = [
  "1st Year", "2nd Year", "3rd Year", "4th Year", "5th Year", "Graduate", "Post-Graduate",
];

const AcademicProfileCard = ({ user, onEdit }) => {
  const hasData = user.course || user.yearLevel || (user.interests && user.interests.length > 0);

  return (
    <div className="bg-white border border-slate-100 p-6 md:p-8 rounded-3xl shadow-sm">
      <div className="flex justify-between items-center mb-8">
        <h3 className="font-bold text-slate-800 text-lg md:text-xl flex items-center gap-3">
          <div className="w-8 h-8 md:w-10 md:h-10 rounded-full bg-indigo-50 flex items-center justify-center text-indigo-600">
            <i className="fa-solid fa-graduation-cap" />
          </div>
          Academic Profile
        </h3>
        <button
          onClick={onEdit}
          className="px-3 py-1.5 md:px-4 md:py-2 bg-slate-50 text-slate-600 hover:bg-slate-100 hover:text-indigo-600 rounded-lg md:rounded-xl text-xs md:text-sm font-bold transition flex items-center gap-1 md:gap-2 border border-slate-200"
        >
          <i className="fa-solid fa-pen-to-square" />
          Edit <span className="hidden sm:inline">Details</span>
        </button>
      </div>

      {!hasData ? (
        <div className="flex flex-col items-center justify-center py-6 text-center gap-2">
          <div className="w-14 h-14 rounded-2xl bg-indigo-50 flex items-center justify-center text-indigo-300 text-2xl mb-2">
            <i className="fa-solid fa-book-open" />
          </div>
          <p className="text-slate-400 text-sm font-medium">No academic info yet.</p>
          <button onClick={onEdit} className="text-indigo-500 hover:text-indigo-700 text-xs font-bold underline underline-offset-2 transition">
            Add your course &amp; interests →
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-6">
          <div className="space-y-2">
            <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Course / Program</label>
            <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 text-slate-700 font-semibold min-h-[52px]">
              {user.course || <span className="text-slate-300">Not set</span>}
            </div>
          </div>

          <div className="space-y-2">
            <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Year Level</label>
            <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 text-slate-700 font-semibold min-h-[52px]">
              {user.yearLevel || <span className="text-slate-300">Not set</span>}
            </div>
          </div>

          <div className="space-y-2 md:col-span-2">
            <label className="text-xs font-bold text-slate-400 uppercase tracking-wider">Subject Interests</label>
            {user.interests && user.interests.length > 0 ? (
              <div className="flex flex-wrap gap-2 p-3 bg-slate-50 rounded-xl border border-slate-200 min-h-[52px]">
                {user.interests.map((tag) => (
                  <span key={tag} className="px-3 py-1 bg-indigo-50 text-indigo-700 border border-indigo-100 rounded-full text-xs font-bold">
                    {tag}
                  </span>
                ))}
              </div>
            ) : (
              <div className="p-4 bg-slate-50 rounded-xl border border-slate-200 text-slate-300 font-semibold min-h-[52px]">
                No interests added
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// ============================================================
// ✅ ACADEMIC MODAL (edit)
// ============================================================
const AcademicModal = ({ onClose, onSave, initialCourse, initialYearLevel, initialInterests }) => {
  const [course, setCourse] = useState(initialCourse);
  const [yearLevel, setYearLevel] = useState(initialYearLevel);
  const [interests, setInterests] = useState([...initialInterests]);
  const [tagInput, setTagInput] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const addTag = () => {
    const tag = tagInput.trim().slice(0, 30);
    if (!tag) return;
    if (interests.length >= 10) return;
    if (interests.some((t) => t.toLowerCase() === tag.toLowerCase())) {
      setTagInput("");
      return;
    }
    setInterests((prev) => [...prev, tag]);
    setTagInput("");
  };

  const removeTag = (idx) => setInterests((prev) => prev.filter((_, i) => i !== idx));

  const handleKeyDown = (e) => {
    if (e.key === "Enter") { e.preventDefault(); addTag(); }
    if (e.key === "Backspace" && tagInput === "" && interests.length > 0) {
      removeTag(interests.length - 1);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSaving(true);
    try {
      await onSave({
        course: course.trim().slice(0, 30),
        yearLevel,
        interests: interests.map((t) => t.trim().slice(0, 30)).filter(Boolean).slice(0, 10),
      });
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-slate-900/60 backdrop-blur-sm animate-fade-in">
      <div className="bg-white rounded-3xl shadow-2xl w-full max-w-lg overflow-hidden border border-slate-100">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-100 flex justify-between items-center">
          <h3 className="text-lg font-bold text-slate-800 flex items-center gap-2">
            <i className="fa-solid fa-graduation-cap text-indigo-500" /> Academic Profile
          </h3>
          <button onClick={onClose} className="w-8 h-8 rounded-full flex items-center justify-center text-slate-400 hover:bg-slate-100 hover:text-slate-600 transition">
            <i className="fa-solid fa-xmark text-lg" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Course */}
          <div>
            <div className="flex justify-between items-center mb-1.5">
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide">
                Course / Program
              </label>
              <span className="text-xs font-semibold text-slate-400">{course.length}/30</span>
            </div>
            <input
              value={course}
              onChange={(e) => setCourse(e.target.value)}
              maxLength={30}
              placeholder="e.g. BS Computer Science"
              className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none transition text-sm font-semibold text-slate-700 bg-slate-50 focus:bg-white focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10"
            />
          </div>

          {/* Year Level */}
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">
              Year Level
            </label>
            <select
              value={yearLevel}
              onChange={(e) => setYearLevel(e.target.value)}
              className="w-full px-4 py-3 rounded-xl border border-slate-200 outline-none transition text-sm font-semibold text-slate-700 bg-slate-50 focus:bg-white focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/10"
            >
              <option value="">Select year level</option>
              {YEAR_LEVELS.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </div>

          {/* Interests tag input */}
          <div>
            <div className="flex justify-between items-center mb-1.5">
              <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide">
                Subject Interests
                <span className="ml-2 text-slate-300 font-medium normal-case">({interests.length}/10 tags)</span>
              </label>
              {tagInput.length > 0 && (
                <span className="text-xs font-semibold text-indigo-500">{tagInput.length}/30</span>
              )}
            </div>

            {/* Tag display + input */}
            <div
              className="flex flex-wrap gap-2 p-3 rounded-xl border border-slate-200 bg-slate-50 focus-within:border-indigo-500 focus-within:ring-4 focus-within:ring-indigo-500/10 focus-within:bg-white transition min-h-[52px] cursor-text"
              onClick={() => document.getElementById("interest-tag-input").focus()}
            >
              {interests.map((tag, i) => (
                <span key={i} className="flex items-center gap-1 px-2.5 py-1 bg-indigo-100 text-indigo-700 rounded-full text-xs font-bold">
                  {tag}
                  <button type="button" onClick={() => removeTag(i)} className="text-indigo-400 hover:text-indigo-700 transition ml-0.5 leading-none">
                    <i className="fa-solid fa-xmark text-[10px]" />
                  </button>
                </span>
              ))}
              {interests.length < 10 && (
                <input
                  id="interest-tag-input"
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyDown={handleKeyDown}
                  maxLength={30}
                  placeholder={interests.length === 0 ? "Type a subject and press Enter…" : "Add another…"}
                  className="flex-1 min-w-[140px] bg-transparent outline-none text-sm font-semibold text-slate-700 placeholder:text-slate-300"
                />
              )}
            </div>
            <p className="text-[11px] text-slate-400 mt-1.5 pl-1">
              Press <kbd className="px-1 py-0.5 bg-slate-100 rounded text-[10px] font-mono border border-slate-200">Enter</kbd> or{" "}
              <button type="button" onClick={addTag} className="text-indigo-500 font-bold hover:underline">Add</button> to create a tag. Max 10 tags, 30 chars each.
            </p>
          </div>

          {/* Actions */}
          <div className="pt-2 flex gap-3">
            <button type="button" onClick={onClose} className="flex-1 py-3 rounded-xl font-bold text-sm text-slate-500 hover:bg-slate-50 hover:text-slate-700 transition">
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSaving}
              className="flex-1 py-3 rounded-xl font-bold text-sm text-white bg-indigo-600 hover:bg-indigo-700 shadow-lg shadow-indigo-600/20 transition hover:-translate-y-0.5 active:translate-y-0 disabled:opacity-60 disabled:cursor-not-allowed"
            >
              {isSaving ? "Saving…" : "Save Changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// ============================================================
// ✅ BUG REPORT MODAL
// ============================================================
const BugReportModal = ({ isOpen, onClose, user }) => {
  const [category, setCategory] = useState("UI/Visual Glitch");
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  if (!isOpen) return null;

  const categories = [
    { id: "UI/Visual Glitch", label: "UI / Glitch", icon: "fa-palette" },
    { id: "Quiz & Exams", label: "Quiz / Exams", icon: "fa-graduation-cap" },
    { id: "Account & Login", label: "Account", icon: "fa-user-shield" },
    { id: "Performance", label: "Slow / Lag", icon: "fa-gauge-high" },
    { id: "Other", label: "Other", icon: "fa-ellipsis" },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!description.trim()) {
      setErrorMessage("Please provide a description of the issue.");
      return;
    }

    setSubmitting(true);
    setErrorMessage("");

    try {
      await apiClient.post("/support/bug-report", {
        category,
        title: title.trim() || undefined,
        description: description.trim(),
        userEmail: user?.email,
        userName: `${user?.firstName || ""} ${user?.lastName || ""}`.trim() || user?.email,
        pageUrl: window.location.href,
        userAgent: navigator.userAgent,
      });

      Swal.fire({
        icon: "success",
        title: "Report Submitted!",
        text: "Thank you for helping us improve ReviewMate. Our team will review the issue.",
        confirmButtonColor: "#2563eb",
      });

      onClose();
    } catch (err) {
      console.error("Bug report submission error:", err);
      const detail =
        err?.response?.data?.message ||
        err?.response?.data?.error?.message ||
        err?.message ||
        "Failed to send bug report. Please try again.";
      setErrorMessage(detail);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-50 flex items-center justify-center p-4 animate-fade-in overflow-y-auto">
      <div className="bg-white rounded-3xl p-6 md:p-8 max-w-lg w-full shadow-2xl relative border border-slate-100 my-8">
        <button
          onClick={onClose}
          disabled={submitting}
          className="absolute top-6 right-6 text-slate-400 hover:text-slate-600 transition w-8 h-8 rounded-full flex items-center justify-center hover:bg-slate-100 cursor-pointer"
        >
          <i className="fa-solid fa-xmark text-lg"></i>
        </button>

        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 rounded-2xl bg-rose-50 border border-rose-100 flex items-center justify-center text-rose-500 shadow-sm">
            <i className="fa-solid fa-bug text-xl"></i>
          </div>
          <div>
            <h3 className="text-xl font-bold text-slate-800">Report an Issue</h3>
            <p className="text-xs text-slate-400">Help us make ReviewMate better for everyone</p>
          </div>
        </div>

        {errorMessage && (
          <div className="mb-4 p-3.5 bg-red-50 border border-red-200 text-red-600 text-xs rounded-xl flex items-start gap-2.5">
            <i className="fa-solid fa-triangle-exclamation text-sm mt-0.5 shrink-0"></i>
            <div>
              <p className="font-bold">Submission failed</p>
              <p className="font-medium mt-0.5">{errorMessage}</p>
            </div>
          </div>
        )}

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-2">
              Category
            </label>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
              {categories.map((cat) => (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => setCategory(cat.id)}
                  className={`px-3 py-2 rounded-xl text-xs font-bold flex items-center justify-center gap-2 border transition cursor-pointer ${
                    category === cat.id
                      ? "bg-rose-50 border-rose-300 text-rose-600 shadow-sm"
                      : "bg-slate-50 border-slate-200 text-slate-600 hover:bg-slate-100"
                  }`}
                >
                  <i className={`fa-solid ${cat.icon} text-[11px]`}></i>
                  <span>{cat.label}</span>
                </button>
              ))}
            </div>
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">
              Summary <span className="text-slate-400 font-normal">(Optional)</span>
            </label>
            <input
              type="text"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="e.g. Broken button or score not updating"
              className="w-full px-4 py-2.5 rounded-xl border border-slate-200 text-sm font-semibold text-slate-700 bg-slate-50 focus:bg-white focus:border-rose-500 focus:ring-4 focus:ring-rose-500/10 outline-none transition"
            />
          </div>

          <div>
            <label className="block text-xs font-bold text-slate-500 uppercase tracking-wide mb-1.5">
              Description <span className="text-rose-500">*</span>
            </label>
            <textarea
              required
              rows={4}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Please describe what happened, steps to reproduce, or any unexpected behavior..."
              className="w-full px-4 py-3 rounded-xl border border-slate-200 text-sm font-medium text-slate-700 bg-slate-50 focus:bg-white focus:border-rose-500 focus:ring-4 focus:ring-rose-500/10 outline-none transition resize-none"
            />
            <div className="flex justify-between items-center text-[11px] text-slate-400 mt-1">
              <span>Be as specific as possible</span>
              <span>{description.length} characters</span>
            </div>
          </div>

          <div className="bg-slate-50 border border-slate-100 p-3 rounded-xl flex items-center justify-between text-xs text-slate-500">
            <span className="flex items-center gap-2">
              <i className="fa-regular fa-envelope text-slate-400"></i>
              <span>Sending from: <strong className="text-slate-700">{user?.email}</strong></span>
            </span>
            <span className="text-[11px] text-slate-400 bg-white px-2 py-0.5 rounded border border-slate-200">
              Auto-attached
            </span>
          </div>

          <div className="pt-2 flex gap-3">
            <button
              type="button"
              onClick={onClose}
              disabled={submitting}
              className="flex-1 py-3 rounded-xl font-bold text-sm text-slate-500 hover:bg-slate-50 hover:text-slate-700 transition cursor-pointer"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={submitting || !description.trim()}
              className={`flex-1 py-3 rounded-xl font-bold text-sm text-white shadow-lg transition flex items-center justify-center gap-2 ${
                submitting || !description.trim()
                  ? "bg-rose-300 cursor-not-allowed shadow-rose-200/20"
                  : "bg-rose-600 hover:bg-rose-700 shadow-rose-600/20 hover:-translate-y-0.5 active:translate-y-0 cursor-pointer"
              }`}
            >
              {submitting ? (
                <>
                  <i className="fa-solid fa-spinner fa-spin"></i>
                  <span>Submitting...</span>
                </>
              ) : (
                <>
                  <i className="fa-solid fa-paper-plane"></i>
                  <span>Submit Bug Report</span>
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
